import { CommonModule } from '@angular/common'
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { Router } from '@angular/router'
import { HocuspocusProvider } from '@hocuspocus/provider'
import { TranslateService } from '@ngx-translate/core'
import { AnyExtension, Editor } from '@tiptap/core'
import Collaboration from '@tiptap/extension-collaboration'
import CollaborationCaret from '@tiptap/extension-collaboration-caret'
import { TiptapBubbleMenuDirective, TiptapEditorDirective } from 'ngx-tiptap'
import { Subscription } from 'rxjs'

import * as Y from 'yjs'
import { EDITOR_STRUCTURAL_EXTENSIONS } from '../../../../../backend/src/tiptap/editor-extensions'
import { Call } from '../../calls.service'
import { UsersService } from '../../users.service'
import { EditorSettingsService } from '../editor/editor-settings.service'
import { CitationExtension } from './extensions/citation-extension'
import { SseSyncProvider } from './sse-sync-provider'
import { hslToHex } from './utils/hsl-to-hex'

@Component({
  selector: 'app-tiptap-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, TiptapEditorDirective, TiptapBubbleMenuDirective],
  templateUrl: './tiptap-editor.component.html',
  styleUrl: './tiptap-editor.component.scss',
})
export class TiptapEditorComponent implements OnInit, OnDestroy {
  @Input({ required: true })
  call!: Call

  @Input({ required: true })
  canUserEdit!: boolean

  @Output()
  openDocument = new EventEmitter<{ resourceId: number; resourcePageNumber: number }>()

  @Output()
  openMeeting = new EventEmitter<void>()

  usersService = inject(UsersService)
  translateService = inject(TranslateService)
  editorSettingsService = inject(EditorSettingsService)
  router = inject(Router)

  document: Y.Doc | null = null

  hocuspocus: HocuspocusProvider | null = null

  editor: Editor | null = null

  isLoading = true
  hasError = false

  private citationsSubscription: Subscription | null = null

  ngOnInit(): void {
    const user = this.usersService.currentUser$.value
    const name = user !== null ? `${user.firstName} ${user.lastName}` : 'Unknown User'

    this.document = new Y.Doc()

    this.hocuspocus = new SseSyncProvider({
      name: `minutes:${this.call.id}`,
      document: this.document,
    })

    const extensions: AnyExtension[] = [
      ...EDITOR_STRUCTURAL_EXTENSIONS,
      Collaboration.configure({
        document: this.document,
      }),
      CollaborationCaret.configure({
        provider: this.hocuspocus,
        user: {
          name,
          color: hslToHex(Math.floor(Math.random() * 360), 0.75, 0.8),
        },
      }),
    ]

    if (this.call.minutesCitations !== null) {
      extensions.push(
        CitationExtension.configure({
          citations: this.call.minutesCitations,
          resources: this.call.userUploadedResources.map((resource) => ({ id: resource.id, fileName: resource.fileName })),
          formatPageLabel: (page: number) => this.translateService.instant('EDITOR.CITATIONS.PAGE_NUMBER', { pageNumber: page }),
          onOpenDocument: (event) => this.openDocument.emit(event),
          onOpenMeeting: (event: { startSentenceId: string; endSentenceId: string }) => {
            this.router.navigate([], {
              queryParams: {
                sentenceId: event.startSentenceId,
                endSentenceId: event.endSentenceId,
                highlight: true,
              },
              queryParamsHandling: 'merge',
            })
            this.openMeeting.emit()
          },
        })
      )
    }

    this.editor = new Editor({ extensions })
    this.editor?.setEditable(this.canUserEdit)

    if (this.call.minutesCitations !== null) {
      this.citationsSubscription = this.editorSettingsService.showCitations$.subscribe((showCitations) => {
        const dom = this.editor?.view.dom ?? null

        if (dom === null) {
          return
        }

        if (showCitations) {
          dom.classList.remove('citations-hidden')
        } else {
          dom.classList.add('citations-hidden')
        }
      })
    }
  }

  ngOnDestroy(): void {
    this.citationsSubscription?.unsubscribe()
    this.editor?.destroy()
    this.hocuspocus?.destroy()
    this.document?.destroy()
  }

  toggleBoldOnSelection() {
    this.editor?.chain().focus().toggleBold().run()
  }

  toggleItalicOnSelection() {
    this.editor?.chain().focus().toggleItalic().run()
  }

  toggleBulletList() {
    this.editor?.chain().focus().toggleBulletList().run()
  }

  toggleOrderedList() {
    this.editor?.chain().focus().toggleOrderedList().run()
  }
}
